import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Ticket, MessageSquare, Clock, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTickets = () => {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState('');
  const [status, setStatus] = useState('open');

  const fetchTickets = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/support-tickets`, { headers: { Authorization: `Bearer ${token}` } }); setTickets(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleRespond = async () => {
    if (!selectedTicket) return;
    try {
      await axios.put(`${API_URL}/api/support-tickets/${selectedTicket.id}/respond?status=${status}&response=${encodeURIComponent(response)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Response sent!'); setSelectedTicket(null); setResponse(''); fetchTickets();
    } catch (e) { toast.error('Failed'); }
  };

  const getStatusBadge = (s) => {
    const styles = { open: 'bg-primary/10 text-primary', in_progress: 'bg-yellow-100 text-yellow-700', resolved: 'bg-green-100 text-green-700', closed: 'bg-slate-100 text-slate-700' };
    return styles[s] || '';
  };

  return (
    <AdminLayout title="Support Tickets">
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-primary">{tickets.filter(t => t.status === 'open').length}</p><p className="text-sm text-muted-foreground">Open</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-yellow-600">{tickets.filter(t => t.status === 'in_progress').length}</p><p className="text-sm text-muted-foreground">In Progress</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-600">{tickets.filter(t => t.status === 'resolved').length}</p><p className="text-sm text-muted-foreground">Resolved</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{tickets.length}</p><p className="text-sm text-muted-foreground">Total</p></CardContent></Card>
      </div>

      {loading ? (<div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-24"></CardContent></Card>)}</div>) : tickets.length > 0 ? (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <Card key={ticket.id} data-testid={`ticket-${ticket.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${ticket.status === 'resolved' ? 'bg-green-100' : 'bg-primary/10'}`}>
                    {ticket.status === 'resolved' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Ticket className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{ticket.subject}</h4>
                      <Badge className={getStatusBadge(ticket.status)}>{ticket.status.replace('_', ' ')}</Badge>
                      <Badge variant="outline">{ticket.category}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{ticket.description}</p>
                    {ticket.admin_response && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-sm"><strong>Response:</strong> {ticket.admin_response}</div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Clock className="w-3 h-3" />{ticket.created_at?.split('T')[0]}</p>
                  </div>
                  {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                    <Button size="sm" onClick={() => { setSelectedTicket(ticket); setStatus(ticket.status); }}>Respond</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (<Card><CardContent className="py-16 text-center"><Ticket className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No Tickets</h3></CardContent></Card>)}

      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Respond to Ticket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <h4 className="font-semibold">{selectedTicket?.subject}</h4>
              <p className="text-sm text-muted-foreground">{selectedTicket?.description}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['open', 'in_progress', 'resolved', 'closed'].map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Response</label>
              <Textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={4} placeholder="Your response..." />
            </div>
            <Button onClick={handleRespond} className="w-full">Send Response</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminTickets;
